import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import {
  mediaBatchService,
  MediaBatchDetail as BatchDetail,
  buildImageUrl,
} from '../../services/api';
import ConfirmModal from '../../components/common/ConfirmModal';
import AttachToPropertyModal from '../../components/admin/AttachToPropertyModal';
import WatermarkConfigModal from '../../components/admin/WatermarkConfigModal';
import AIEditModal from '../../components/admin/AIEditModal';

const Container = styled.div`
  padding: 2rem;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0;
  margin-bottom: 0.5rem;
`;

const Title = styled.h1`
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
`;

const SubtleLink = styled.button`
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0;
  margin-top: 0.5rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;
  }
`;

const Description = styled.p`
  color: #666;
  margin: 0.25rem 0 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'ghost' | 'danger' }>`
  padding: 0.65rem 1.3rem;
  border-radius: 6px;
  font-size: 0.92rem;
  cursor: pointer;
  border: ${({ $variant }) => ($variant === 'ghost' ? '1px solid #cbd5e1' : 'none')};
  font-weight: 500;
  background: ${({ $variant, theme }) =>
    $variant === 'secondary'
      ? '#e2e8f0'
      : $variant === 'ghost'
      ? 'white'
      : $variant === 'danger'
      ? '#ef4444'
      : theme.colors.primary};
  color: ${({ $variant }) => ($variant === 'secondary' || $variant === 'ghost' ? '#1e293b' : 'white')};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Dropzone = styled.div<{ $dragging: boolean }>`
  border: 2px dashed ${({ $dragging, theme }) => ($dragging ? theme.colors.primary : '#cbd5e1')};
  background: ${({ $dragging, theme }) => ($dragging ? `${theme.colors.primary}10` : '#f8fafc')};
  border-radius: 10px;
  padding: 2.5rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1.5rem;

  .icon {
    font-size: 2.5rem;
  }
  .hint {
    color: #64748b;
    margin-top: 0.5rem;
  }
  input {
    display: none;
  }
`;

const ProgressWrap = styled.div`
  margin: 1rem 0;
`;

const ProgressBar = styled.div`
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.2s;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const SelectionInfo = styled.div`
  color: #475569;
  font-size: 0.9rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
`;

const Thumb = styled.div<{ $selected: boolean }>`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 3px solid
    ${({ $selected, theme }) => ($selected ? theme.colors.primary : 'transparent')};
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  background: #f1f5f9;

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
    display: block;
  }
`;

const Check = styled.div<{ $selected: boolean }>`
  position: absolute;
  top: 6px;
  left: 6px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ $selected, theme }) => ($selected ? theme.colors.primary : 'rgba(255,255,255,0.85)')};
  border: 2px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.8rem;
`;

const RemoveBtn = styled.button`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  line-height: 1;
`;

const AiBtn = styled.button`
  position: absolute;
  bottom: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.92);
  color: ${({ theme }) => theme.colors.primaryDark};
  border: none;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.shadows.sm};

  &:hover {
    background: #fff;
  }
`;

const AiBadge = styled.span`
  position: absolute;
  top: 6px;
  left: 34px;
  padding: 0.15rem 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: #94a3b8;
`;

const AdminMediaBatchDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const batchId = Number(id);
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [batch, setBatch] = useState<BatchDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [watermarkOpen, setWatermarkOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteImageId, setDeleteImageId] = useState<number | null>(null);
  const [reapplying, setReapplying] = useState(false);
  const [cacheBust, setCacheBust] = useState(0);
  const [aiImage, setAiImage] = useState<BatchDetail['images'][number] | null>(null);

  const fetchBatch = useCallback(async () => {
    try {
      setLoading(true);
      const data = await mediaBatchService.get(batchId);
      setBatch(data);
    } catch (error) {
      showNotification('Lote não encontrado', 'error');
      navigate('/admin/media-batches');
    } finally {
      setLoading(false);
    }
  }, [batchId, navigate, showNotification]);

  useEffect(() => {
    if (batchId) fetchBatch();
  }, [batchId, fetchBatch]);

  const doUpload = async (files: File[]) => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) {
      showNotification('Selecione arquivos de imagem', 'error');
      return;
    }
    try {
      setUploading(true);
      setProgress(0);
      const updated = await mediaBatchService.uploadFiles(batchId, images, setProgress);
      setBatch(updated);
      showNotification(`${images.length} foto(s) enviada(s) com marca d'água`, 'success');
    } catch (error) {
      showNotification('Erro ao enviar as fotos', 'error');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    doUpload(Array.from(e.dataTransfer.files));
  };

  const toggleSelect = (imageId: number) =>
    setSelected((prev) =>
      prev.includes(imageId) ? prev.filter((i) => i !== imageId) : [...prev, imageId]
    );

  const handleDownload = async () => {
    if (!batch) return;
    try {
      setDownloading(true);
      // Se houver seleção, baixa apenas as selecionadas; senão, todas
      const ids = selected.length ? selected : undefined;
      const suffix = ids ? `_selecionadas` : '';
      await mediaBatchService.downloadZip(batch.id, `${batch.name || 'lote'}${suffix}.zip`, ids);
    } catch (error) {
      showNotification('Erro ao baixar o lote', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleReapply = async () => {
    if (!batch) return;
    try {
      setReapplying(true);
      const result = await mediaBatchService.reapplyWatermark(batch.id);
      showNotification(result.message, 'success');
      await fetchBatch();
      setCacheBust(Date.now()); // força recarregar thumbnails atualizados
    } catch (error: any) {
      showNotification(
        error?.response?.data?.detail || 'Erro ao reaplicar a marca d\'água',
        'error'
      );
    } finally {
      setReapplying(false);
    }
  };

  const handleDeleteImage = async () => {
    if (deleteImageId == null || !batch) return;
    try {
      await mediaBatchService.deleteImage(batch.id, deleteImageId);
      setSelected((prev) => prev.filter((i) => i !== deleteImageId));
      setDeleteImageId(null);
      fetchBatch();
      showNotification('Imagem removida', 'success');
    } catch (error) {
      showNotification('Erro ao remover a imagem', 'error');
    }
  };

  if (loading || !batch) {
    return (
      <Container>
        <EmptyState>Carregando...</EmptyState>
      </Container>
    );
  }

  const hasImages = batch.images.length > 0;
  const hasUnwatermarked = batch.images.some((img) => !img.watermarked);
  const selectedCount = selected.length;

  return (
    <Container>
      <TopBar>
        <div>
          <BackLink onClick={() => navigate('/admin/media-batches')}>← Voltar aos lotes</BackLink>
          <Title>{batch.name}</Title>
          {batch.description && <Description>{batch.description}</Description>}
          <SubtleLink onClick={() => setWatermarkOpen(true)}>⚙️ Configurar marca d'água</SubtleLink>
        </div>
        <Actions>
          {hasUnwatermarked && (
            <Button $variant="ghost" onClick={handleReapply} disabled={reapplying}>
              {reapplying ? 'Aplicando...' : '💧 Aplicar marca d\'água nas existentes'}
            </Button>
          )}
          <Button $variant="secondary" onClick={handleDownload} disabled={!hasImages || downloading}>
            {downloading
              ? 'Gerando ZIP...'
              : selectedCount
              ? `⬇️ Baixar selecionadas (${selectedCount})`
              : '⬇️ Baixar todas (ZIP)'}
          </Button>
          <Button onClick={() => setAttachOpen(true)} disabled={!hasImages}>
            🏠 Usar em imóvel
          </Button>
        </Actions>
      </TopBar>

      <Dropzone
        $dragging={dragging}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <div className="icon">📤</div>
        <div>
          <strong>Arraste as fotos aqui</strong> ou clique para selecionar
        </div>
        <div className="hint">
          A marca d'água é aplicada automaticamente. Você pode enviar várias de uma vez.
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => {
            if (e.target.files) doUpload(Array.from(e.target.files));
            e.target.value = '';
          }}
        />
      </Dropzone>

      {uploading && (
        <ProgressWrap>
          <ProgressBar>
            <ProgressFill $pct={progress} />
          </ProgressBar>
          <SelectionInfo>Enviando e aplicando marca d'água... {progress}%</SelectionInfo>
        </ProgressWrap>
      )}

      <Toolbar>
        <SelectionInfo>
          {batch.images_count} foto{batch.images_count !== 1 ? 's' : ''} no lote
          {selected.length > 0 && ` • ${selected.length} selecionada(s)`}
        </SelectionInfo>
        {hasImages && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {selected.length > 0 && (
              <Button $variant="ghost" onClick={() => setSelected([])}>
                Limpar seleção
              </Button>
            )}
            <Button
              $variant="ghost"
              onClick={() =>
                setSelected(selected.length === batch.images.length ? [] : batch.images.map((i) => i.id))
              }
            >
              {selected.length === batch.images.length ? 'Desmarcar todas' : 'Selecionar todas'}
            </Button>
          </div>
        )}
      </Toolbar>

      {hasImages ? (
        <Grid>
          {batch.images.map((img) => {
            // A posição na seleção é a ordem em que a foto entra no imóvel.
            const selectionIndex = selected.indexOf(img.id);
            const isSelected = selectionIndex >= 0;
            return (
              <Thumb key={img.id} $selected={isSelected} onClick={() => toggleSelect(img.id)}>
                <Check
                  $selected={isSelected}
                  title={isSelected ? `${selectionIndex + 1}ª foto do imóvel` : undefined}
                >
                  {isSelected ? selectionIndex + 1 : ''}
                </Check>
                {img.ai_edited && <AiBadge>IA</AiBadge>}
                <RemoveBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteImageId(img.id);
                  }}
                  title="Remover do lote"
                >
                  ×
                </RemoveBtn>
                <AiBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    setAiImage(img);
                  }}
                  title="Editar com IA"
                >
                  ✨ IA
                </AiBtn>
                <img
                  src={
                    buildImageUrl(img.thumbnail_url || img.url) +
                    (cacheBust ? `?v=${cacheBust}` : '')
                  }
                  alt={img.original_filename || ''}
                />
              </Thumb>
            );
          })}
        </Grid>
      ) : (
        <EmptyState>Nenhuma foto ainda. Envie as primeiras acima.</EmptyState>
      )}

      <AttachToPropertyModal
        isOpen={attachOpen}
        onClose={() => setAttachOpen(false)}
        batchId={batch.id}
        selectedImageIds={selected}
        totalImages={batch.images_count}
        onAttached={() => setSelected([])}
      />

      <WatermarkConfigModal isOpen={watermarkOpen} onClose={() => setWatermarkOpen(false)} />

      <AIEditModal
        isOpen={aiImage != null}
        onClose={() => {
          setAiImage(null);
          setCacheBust(Date.now());
        }}
        batchId={batch.id}
        image={aiImage}
        onDone={(updated) => setBatch(updated)}
      />

      <ConfirmModal
        isOpen={deleteImageId != null}
        onClose={() => setDeleteImageId(null)}
        onConfirm={handleDeleteImage}
        title="Remover imagem"
        message="Remover esta imagem do lote? Caso ela já tenha sido usada em algum imóvel, continuará lá."
        confirmText="Sim, remover"
        cancelText="Cancelar"
        type="warning"
      />
    </Container>
  );
};

export default AdminMediaBatchDetail;
